import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, X } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Selo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { BuscaRemota, Rotulo, Selecao, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { del, mensagemDeErro, patch, post } from '../../nucleo/api';
import { dinheiro, nomePessoa, numero } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { CamposLocais, ErroGeral, useEnvio } from './formulario-local';
import { COR_TIPO_BENEFICIO, TIPOS_BENEFICIO, invalidarFolha, type Beneficio } from './tipos';

type Alvo = { id: string; nome: string };

/** Escolha de funcionários ou funções que recebem o benefício: busca remota e lista de escolhidos. */
function SeletorAlvos({ aplicaA, alvos, aoMudar }: { aplicaA: 'funcionario' | 'funcao'; alvos: Alvo[]; aoMudar: (a: Alvo[]) => void }) {
  const { t } = useTranslation('folha');
  const [rodada, setRodada] = useState(0);
  // Opção de nomes do modo discreto: só nome de funcionário vira iniciais (nome de função fica como está).
  const nomeAlvo = (n: string) => (aplicaA === 'funcionario' ? nomePessoa(n) : n);
  const def = useMemo(
    () =>
      aplicaA === 'funcionario'
        ? { caminho: '/pessoas/funcionarios', params: { estado: 'ativo' }, rotulo: (i: Record<string, unknown>) => String(i.nome) }
        : { caminho: '/pessoas/funcoes', rotulo: (i: Record<string, unknown>) => String(i.nome) },
    [aplicaA],
  );
  return (
    <div className="flex flex-col gap-2">
      <Rotulo id="alvo-busca" rotulo={aplicaA === 'funcionario' ? t('beneficios.adicionarFuncionario') : t('beneficios.adicionarFuncao')} obrigatorio>
        <BuscaRemota
          key={`${aplicaA}-${rodada}`}
          id="alvo-busca"
          def={def}
          valor={null}
          aoMudar={(v, item) => {
            if (!v || !item) return;
            if (!alvos.some((a) => a.id === v)) aoMudar([...alvos, { id: v, nome: String(item.nome) }]);
            setRodada((r) => r + 1);
          }}
        />
      </Rotulo>
      {alvos.length === 0 ? (
        <p className="text-xs text-tinta-3">{t('beneficios.semAlvos')}</p>
      ) : (
        <ul className="flex flex-wrap gap-2" aria-label={t('beneficios.alvos')}>
          {alvos.map((a) => (
            <li key={a.id} className="inline-flex min-w-0 items-center gap-1 rounded-full border border-borda bg-sup-2 py-1 pr-1 pl-3 text-sm">
              <span className="truncate" title={nomeAlvo(a.nome)}>{nomeAlvo(a.nome)}</span>
              <button type="button" aria-label={t('beneficios.removerAlvo', { nome: nomeAlvo(a.nome) })} onClick={() => aoMudar(alvos.filter((x) => x.id !== a.id))} className="rounded-full p-1 text-tinta-3 hover:text-perigo">
                <X className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function valoresIniciais(b: Beneficio | null): Record<string, unknown> {
  return b
    ? { nome: b.nome, tipo: b.tipo, forma: b.forma, valor_centavos: b.valor_centavos, percentual: b.percentual, desconto_forma: b.desconto_forma, desconto_valor_centavos: b.desconto_valor_centavos, desconto_percentual: b.desconto_percentual, ativo: b.ativo }
    : { tipo: 'vale_refeicao', forma: 'valor', desconto_forma: 'nenhum', ativo: true };
}

function FormBeneficio({ beneficio, aoSalvar }: { beneficio: Beneficio | null; aoSalvar: () => void }) {
  const { t } = useTranslation('folha');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [valores, setValores] = useState<Record<string, unknown>>(() => valoresIniciais(beneficio));
  const [aplicaA, setAplicaA] = useState<'funcionario' | 'funcao'>(beneficio?.aplica_a ?? 'funcao');
  const [alvos, setAlvos] = useState<Alvo[]>(beneficio?.alvos ?? []);
  const envio = useEnvio();
  const campos: DefCampo[] = [
    { nome: 'nome', rotulo: t('campos.nome'), tipo: 'texto', obrigatorio: true },
    { nome: 'tipo', rotulo: t('campos.tipo'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes: TIPOS_BENEFICIO.map((v) => ({ valor: v, rotulo: t(`tipoBeneficio.${v}`) })) },
    { nome: 'forma', rotulo: t('campos.formaBeneficio'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes: [{ valor: 'valor', rotulo: t('forma.valorMensal') }, { valor: 'percentual', rotulo: t('forma.percentualSalario') }] },
    { nome: 'valor_centavos', rotulo: t('campos.valorMensal'), tipo: 'dinheiro', obrigatorio: true, largura: 'meia', visivel: (v) => v.forma === 'valor' },
    { nome: 'percentual', rotulo: t('campos.percentualSalario'), tipo: 'percentual', obrigatorio: true, largura: 'meia', min: 0, max: 100, passo: 0.01, visivel: (v) => v.forma === 'percentual' },
    {
      nome: 'desconto_forma',
      rotulo: t('campos.descontoFuncionario'),
      tipo: 'selecao',
      obrigatorio: true,
      largura: 'meia',
      ajuda: t('campos.descontoAjuda'),
      opcoes: [{ valor: 'nenhum', rotulo: t('forma.semDesconto') }, { valor: 'valor', rotulo: t('forma.valorMensal') }, { valor: 'percentual', rotulo: t('forma.percentualSalario') }],
    },
    { nome: 'desconto_valor_centavos', rotulo: t('campos.descontoValor'), tipo: 'dinheiro', obrigatorio: true, largura: 'meia', visivel: (v) => v.desconto_forma === 'valor' },
    { nome: 'desconto_percentual', rotulo: t('campos.descontoPercentual'), tipo: 'percentual', obrigatorio: true, largura: 'meia', min: 0, max: 100, passo: 0.01, visivel: (v) => v.desconto_forma === 'percentual' },
    { nome: 'ativo', rotulo: t('campos.ativo'), tipo: 'booleano', largura: 'meia' },
  ];

  async function salvar() {
    const v = valores;
    if (!envio.validar(campos, v, alvos.length ? {} : { alvo_ids: tn('erros.codigos.beneficio.alvo_obrigatorio') })) return;
    const corpo = {
      nome: v.nome,
      tipo: v.tipo,
      forma: v.forma,
      valor_centavos: v.forma === 'valor' ? (v.valor_centavos ?? null) : null,
      percentual: v.forma === 'percentual' ? (v.percentual ?? null) : null,
      desconto_forma: v.desconto_forma,
      desconto_valor_centavos: v.desconto_forma === 'valor' ? (v.desconto_valor_centavos ?? null) : null,
      desconto_percentual: v.desconto_forma === 'percentual' ? (v.desconto_percentual ?? null) : null,
      aplica_a: aplicaA,
      alvo_ids: alvos.map((a) => a.id),
      ativo: !!v.ativo,
    };
    const ok = await envio.enviar(async () => {
      if (beneficio) await patch(`/folha/beneficios/${beneficio.id}`, corpo);
      else await post('/folha/beneficios', corpo);
    });
    if (ok) {
      await invalidarFolha(qc);
      toast.sucesso(tn('comum.salvo'));
      aoSalvar();
    }
  }

  return (
    <form
      id="form-beneficio"
      noValidate
      className="grid grid-cols-1 gap-4 sm:grid-cols-6"
      onSubmit={(e) => {
        e.preventDefault();
        void salvar();
      }}
    >
      <ErroGeral texto={envio.geral} />
      <CamposLocais campos={campos} valores={valores} erros={envio.erros} aoMudar={(n, x) => setValores((a) => ({ ...a, [n]: x }))} />
      <div className="sm:col-span-6">
        <Rotulo id="aplica-a" rotulo={t('campos.aplicaA')} obrigatorio erro={envio.erros.alvo_ids}>
          <Selecao
            id="aplica-a"
            opcoes={[{ valor: 'funcao', rotulo: t('aplicaA.funcao') }, { valor: 'funcionario', rotulo: t('aplicaA.funcionario') }]}
            value={aplicaA}
            onChange={(e) => {
              setAplicaA(e.target.value as 'funcionario' | 'funcao');
              setAlvos([]);
            }}
          />
        </Rotulo>
      </div>
      <div className="sm:col-span-6">
        <SeletorAlvos aplicaA={aplicaA} alvos={alvos} aoMudar={setAlvos} />
      </div>
    </form>
  );
}

function valorBeneficio(b: Beneficio) {
  return b.forma === 'valor' ? dinheiro(b.valor_centavos, b.moeda) : `${numero(b.percentual ?? 0, 2)}%`;
}

function descontoBeneficio(b: Beneficio, semDesconto: string) {
  if (b.desconto_forma === 'nenhum') return semDesconto;
  return b.desconto_forma === 'valor' ? dinheiro(b.desconto_valor_centavos, b.moeda) : `${numero(b.desconto_percentual ?? 0, 2)}%`;
}

/** M8.1: benefícios por funcionário ou por função, com o desconto do funcionário. */
// Val5 (29/10): eventos e benefícios são configuração da folha (`folha.configurar`).
export function Beneficios() {
  const { t } = useTranslation('folha');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [aberto, setAberto] = useState<Beneficio | 'novo' | null>(null);
  const [remover, setRemover] = useState<Beneficio | null>(null);
  const atual = aberto && aberto !== 'novo' ? aberto : null;

  const excluir = useMutation({
    mutationFn: (id: string) => del(`/folha/beneficios/${id}`),
    onSuccess: async () => {
      setRemover(null);
      setAberto(null);
      await invalidarFolha(qc);
    },
    onError: (e) => {
      setRemover(null);
      toast.erro(mensagemDeErro(e));
    },
  });

  const colunas: Coluna<Beneficio>[] = [
    { chave: 'nome', rotulo: t('campos.nome'), ordenavel: false, principal: true, render: (b) => <span className="font-medium">{b.nome}</span> },
    { chave: 'tipo', rotulo: t('campos.tipo'), render: (b) => <Selo cor={COR_TIPO_BENEFICIO[b.tipo]}>{t(`tipoBeneficio.${b.tipo}`)}</Selo> },
    { chave: 'valor', rotulo: t('campos.valor'), alinhar: 'direita', mono: true, render: valorBeneficio },
    { chave: 'desconto', rotulo: t('campos.descontoFuncionario'), alinhar: 'direita', mono: true, render: (b) => descontoBeneficio(b, t('forma.semDesconto')) },
    {
      chave: 'alvos',
      rotulo: t('campos.aplicaA'),
      render: (b) => (
        <span className="block max-w-72 truncate" title={b.alvos.map((a) => (b.aplica_a === 'funcionario' ? nomePessoa(a.nome) : a.nome)).join(', ')}>
          {t(`aplicaA.${b.aplica_a}`)}: {b.alvos.map((a) => (b.aplica_a === 'funcionario' ? nomePessoa(a.nome) : a.nome)).join(', ') || '-'}
        </span>
      ),
    },
    { chave: 'ativo', rotulo: t('campos.situacao'), render: (b) => <Selo cor={b.ativo ? 'verde' : 'neutro'}>{b.ativo ? t('situacao.ativo') : t('situacao.inativo')}</Selo> },
  ];

  return (
    <>
      {s.pode('folha.configurar') && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('novo')}>
            {t('acoes.novoBeneficio')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Tabela<Beneficio> tela="folha.beneficios" caminho="/folha/beneficios" colunas={colunas} aoClicar={(b) => setAberto(b)} vazio={<Vazio texto={t('vazio.beneficios')} />} />
      <Drawer
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        largura="lg"
        titulo={atual ? atual.nome : t('acoes.novoBeneficio')}
        rodape={
          <>
            {atual && s.pode('folha.configurar') && (
              <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={() => setRemover(atual)}>
                {tn('comum.excluir')}
              </Botao>
            )}
            {s.pode('folha.configurar') && (
              <>
                <Botao variante="fantasma" onClick={() => setAberto(null)}>
                  {tn('comum.cancelar')}
                </Botao>
                <Botao variante="primario" type="submit" form="form-beneficio">
                  {tn('comum.salvar')}
                </Botao>
              </>
            )}
          </>
        }
      >
        {aberto && <FormBeneficio key={atual?.id ?? 'novo'} beneficio={atual} aoSalvar={() => setAberto(null)} />}
      </Drawer>
      <Confirmar aberto={!!remover} aoFechar={() => setRemover(null)} aoConfirmar={() => remover && excluir.mutate(remover.id)} titulo={t('confirmar.excluirBeneficio')} texto={t('confirmar.excluirBeneficioTexto')} perigoso carregando={excluir.isPending} />
    </>
  );
}
